/* eslint-disable @typescript-eslint/no-unused-vars */
// ** React Imports
import { SyntheticEvent, useState, useEffect, Fragment } from 'react'

// ** Next Import
import { useRouter } from 'next/router'

// ** MUI Imports
import List from '@mui/material/List'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import { styled, useTheme } from '@mui/material/styles'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import MuiListItem, { ListItemProps } from '@mui/material/ListItem'

// ** Third Party Imports
import clsx from 'clsx'
import { usePopper } from 'react-popper'

// ** Theme Config Import
import themeConfig from 'src/configs/themeConfig'

// ** Types
import { NavGroup } from 'src/@core/layouts/types'
import { Settings } from 'src/@core/context/settingsContext'

// ** Custom Components Imports
import HorizontalNavItems from './HorizontalNavItems'
import Translations from 'src/layouts/components/Translations'
import CanViewNavGroup from 'src/layouts/components/acl/CanViewNavGroup'

// ** Utils
import { hexToRGBA } from 'src/@core/utils/hex-to-rgba'
import { hasSubActiveChild } from 'src/@core/layouts/utils'
import { Icon } from '@iconify/react'
import { Collapse, ListItemIcon, Stack, useMediaQuery } from '@mui/material'

interface Props {
  item: NavGroup
  settings: Settings
  hasParent?: boolean
}

// ** Styled Components
const ListItem = styled(MuiListItem)<ListItemProps>(({ theme }) => ({
  cursor: 'pointer',
  borderRadius: theme.shape.borderRadius
}))

const NavigationMenu = styled(Paper)(({ theme }) => ({
  overflowY: 'auto',
  padding: theme.spacing(2, 0),
  maxHeight: 'calc(100vh - 13rem)',
  backgroundColor: theme.palette.text.primary,
  '& > :not(:last-child)': {
    marginBottom: theme.spacing(1)
  },

  '&::-webkit-scrollbar': {
    width: 6
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: 20,
    background: theme.palette.text.primary
  },
  '&::-webkit-scrollbar-track': {
    borderRadius: 20,
    background: 'transparent'
  },
  '& .MuiList-root': {
    paddingTop: 0,
    paddingBottom: 0
  }
}))

const HorizontalNavGroup = (props: Props) => {
  // ** Props
  const { item, hasParent, settings } = props

  // ** Hooks & Vars
  const theme = useTheme()
  const router = useRouter()
  const currentURL = router.asPath
  const { id } = router.query
  const { direction } = settings
  const { menuTextTruncate, horizontalMenuToggle } = themeConfig

  const popperOffsetHorizontal = direction === 'rtl' ? 16 : -16
  const popperPlacement = direction === 'rtl' ? 'bottom-end' : 'bottom-start'
  const popperPlacementSubMenu = direction === 'rtl' ? 'left-start' : 'right-start'

  // ** States
  const [menuOpen, setMenuOpen] = useState<boolean>(false)
  const [popperElement, setPopperElement] = useState(null)
  const [anchorEl, setAnchorEl] = useState<Element | null>(null)
  const [referenceElement, setReferenceElement] = useState(null)
  const [open, setOpen] = useState<{ [key: string]: boolean }>({})

  const { update } = usePopper(referenceElement, popperElement, {
    placement: hasParent ? popperPlacementSubMenu : popperPlacement,
    strategy: 'fixed',
    modifiers: [
      {
        enabled: true,
        name: 'offset',
        options: {
          offset: hasParent ? [-8, 19] : [popperOffsetHorizontal, -2]
        }
      },
      {
        enabled: true,
        name: 'flip'
      }
    ]
  })

  const handleGroupOpen = (event: SyntheticEvent) => {
    setAnchorEl(event.currentTarget)
    setMenuOpen(true)
    setOpen(prev => ({ ...prev, [item.title]: true }))
    update ? update() : null
  }

  const handleGroupClose = () => {
    setAnchorEl(null)
    setMenuOpen(false)
    setOpen(prev => ({ ...prev, [item.title]: false }))
  }

  const handleMenuToggleOnClick = (event: SyntheticEvent) => {
    if (anchorEl) {
      handleGroupClose()
    } else {
      handleGroupOpen(event)
    }
  }

  useEffect(() => {
    handleGroupClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath])

  const handleToggle = (label: string) => {
    setOpen(prev => ({ ...prev, [label]: !prev[label] }))
  }

  const WrapperCondition = horizontalMenuToggle === 'click'
  const MainWrapper = WrapperCondition ? ClickAwayListener : 'div'
  const ChildWrapper = WrapperCondition ? 'div' : Fragment
  const isMobile = useMediaQuery('(max-width:540px) and (orientation: portrait)')

  return (
    <CanViewNavGroup navGroup={item}>
      {/* @ts-ignore */}
      <MainWrapper
        {...(WrapperCondition ? { onClickAway: handleGroupClose } : { onMouseLeave: handleGroupClose })}
        style={{ width: '100%', padding: isMobile ? '0 8px' : '0 16px' }}
      >
        <ChildWrapper>
          <List>
            <ListItem
              aria-haspopup='true'
              className={clsx('menu-group', { 'Mui-selected': hasSubActiveChild(item, currentURL, id as string) })}
              {...(horizontalMenuToggle === 'click'
                ? {}
                : {
                    onClick: (event: SyntheticEvent) => {
                      handleMenuToggleOnClick(event)
                    }
                  })}
              onClick={() => handleToggle(item.title)}
              sx={{
                ...(menuOpen ? { backgroundColor: 'action.hover' } : {}),
                ...(hasParent
                  ? {
                      '&.Mui-selected': {
                        boxShadow: `0px 2px 6px ${hexToRGBA(theme.palette.text.primary, 0.48)}`,
                        backgroundColor: 'grey.800 !important',
                        '&:hover': {
                          backgroundColor: 'grey.800'
                        }
                      }
                    }
                  : {
                      mx: 2,
                      '&.Mui-selected': {
                        backgroundColor: 'grey.800',
                        '&:hover': {
                          backgroundColor: 'grey.800'
                        },
                        '& .MuiTypography-root': {
                          backgroundColor: 'action.selected'
                        },
                        '& :not(.menu-item-meta) > svg': {
                          color: 'common.white'
                        },
                        '& .menu-item-meta > svg, & .menu-item-meta svg': {
                          color: 'common.white'
                        },
                        '&.Mui-focusVisible': {
                          backgroundColor: 'action.active',
                          '&:hover': {
                            backgroundColor: 'action.active'
                          }
                        }
                      }
                    }),
                flexDirection: 'column'
              }}
            >
              <Stack
                sx={{
                  gap: 2,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  ...(open[item.title] && { paddingBottom: 2 })
                }}
              >
                <Stack
                  sx={{
                    gap: 3,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    ...(menuTextTruncate && { overflow: 'hidden' })
                  }}
                >
                  <ListItemIcon
                    sx={{
                      transition: 'margin .25s ease-in-out',
                      ...(hasParent && item.children ? { ml: 1.5, mr: 3.5 } : {})
                    }}
                  >
                    <Icon icon={item.icon as string} fontSize='24px' color={theme.palette.common.white} />
                  </ListItemIcon>
                  <Typography {...(menuTextTruncate && { noWrap: true })} sx={{ color: 'common.white' }}>
                    <Translations text={item.title} />
                  </Typography>
                </Stack>

                {open[item.title] ? (
                  <Icon icon={'tabler:chevron-up'} fontSize='1.125rem' color={theme.palette.common.white} />
                ) : (
                  <Icon icon={'tabler:chevron-down'} fontSize='1.125rem' color={theme.palette.common.white} />
                )}
              </Stack>

              <Collapse
                in={open[item.title]}
                timeout='auto'
                unmountOnExit
                className='collape-menu'
                sx={{ width: '100%' }}
              >
                <NavigationMenu
                  sx={{
                    ...(hasParent ? { overflowY: 'auto', overflowX: 'visible', maxHeight: 'calc(100vh - 21rem)' } : {})
                  }}
                >
                  <HorizontalNavItems {...props} hasParent horizontalNavItems={item.children} />
                </NavigationMenu>
              </Collapse>
            </ListItem>
          </List>
        </ChildWrapper>
      </MainWrapper>
    </CanViewNavGroup>
  )
}

export default HorizontalNavGroup
